<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
    if (value && typeof value === 'object') return true;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const renderedHeaderTemplate = computed(() => {
  if (headerComponent.value?.format !== 'TEXT' || !headerComponent.value?.text) return '';
  return replaceTemplateVariablesCustom(headerComponent.value.text, processedParams.value.header_text);
});

const replaceTemplateVariablesCustom = (text, paramGroup) => {
  if (!text) return '';
  return text.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariableCustom(variable);
    return paramGroup?.[variableKey] || `{{${variable}}}`;
  });
};

const processVariableCustom = str => str.replace(/{{|}}/g, '');

const isFormInvalid = computed(() => {
  if (!hasVariables.value && !hasMediaHeader.value) return false;

  if (hasMediaHeader.value && !processedParams.value.header?.media_url) {
    return true;
  }

  if (processedParams.value.header_text) {
    const hasEmptyHeaderVariable = Object.values(processedParams.value.header_text).some(
      value => !value
    );
    if (hasEmptyHeaderVariable) return true;
  }

  if (hasVariables.value && processedParams.value.body) {
    const hasEmptyBodyVariable = Object.values(processedParams.value.body).some(
      value => !value
    );
    if (hasEmptyBodyVariable) return true;
  }

  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const formatToBrazilianDate = value => {
  if (typeof value !== 'string') return value;
  // Regex to detect ISO Date (YYYY-MM-DD...)
  const isoDateRegex = /^(\d{4})-(\d{2})-(\d{2})(T\d{2}:\d{2}:\d{2}.*)?$/;
  const match = value.match(isoDateRegex);
  if (match) {
    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
  }
  return value;
};

const getContactFallbackValue = variableName => {
  if (!props.contact) return '';
  const keyMatch = variableName.toLowerCase();
  
  let value = '';

  // 1. Custom Attributes Mapping prefix 'ca_'
  if (keyMatch.startsWith('ca_')) {
    const customAttributeKey = keyMatch.replace('ca_', '');
    const customAttributes = props.contact.custom_attributes || {};
    value = customAttributes[customAttributeKey] || '';
  } 
  // 2. Exact matches for standard fields to avoid partial matches (like company_name matching name)
  else if (['first_name'].includes(keyMatch)) {
    const nameParts = (props.contact.name || '').split(' ');
    value = nameParts[0] || '';
  } else if (['last_name'].includes(keyMatch)) {
    const nameParts = (props.contact.name || '').split(' ');
    value = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
  } else if (['name', 'full_name'].includes(keyMatch)) {
    value = props.contact.name || '';
  } else if (['email'].includes(keyMatch)) {
    value = props.contact.email || '';
  } else if (['phone', 'phone_number'].includes(keyMatch)) {
    value = props.contact.phone_number || '';
  } 
  // 3. Specific Mappings and Fallbacks
  else {
    const customAttributes = props.contact.custom_attributes || {};
    const additionalAttributes = props.contact.additional_attributes || {};
    
    if (['bio', 'biografia', 'description'].includes(keyMatch)) {
      value = props.contact.description || additionalAttributes.description || additionalAttributes.bio || customAttributes.bio || '';
    } else if (['city', 'cidade'].includes(keyMatch)) {
      value = additionalAttributes.city || customAttributes.city || '';
    } else if (['country', 'pais'].includes(keyMatch)) {
      value = additionalAttributes.country || customAttributes.country || '';
    } else if (['company_name', 'company', 'empresa'].includes(keyMatch)) {
      value = additionalAttributes.company_name || customAttributes.company_name || additionalAttributes.company || customAttributes.company || '';
    } else {
      // Final fallback: try exact key match in all attribute objects
      value = customAttributes[variableName] || additionalAttributes[variableName] || '';
    }
  }

  return formatToBrazilianDate(value);
};

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );

  // Auto-fill variables from contact
  if (processedParams.value.body) {
    Object.keys(processedParams.value.body).forEach(key => {
      processedParams.value.body[key] = getContactFallbackValue(key);
    });
  }

  if (processedParams.value.header_text) {
    Object.keys(processedParams.value.header_text).forEach(key => {
      processedParams.value.header_text[key] = getContactFallbackValue(key);
    });
  }
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2">
        <div class="rounded-md">
          <div v-if="renderedHeaderTemplate" class="text-sm font-semibold whitespace-pre-wrap text-n-slate-12 mb-2">
            {{ renderedHeaderTemplate }}
          </div>
          <div class="text-sm whitespace-pre-wrap text-n-slate-12">
            {{ renderedTemplate }}
          </div>
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <div v-if="hasVariables || hasMediaHeader">
      <div v-if="hasMediaHeader" class="mb-4">
        <p class="mb-2.5 text-sm font-semibold">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <div class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
        </div>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Header Variables Section -->
      <div v-if="processedParams.header_text">
        <p class="mb-2.5 text-sm font-semibold">
          Variáveis do Cabeçalho
        </p>
        <div
          v-for="(variable, key) in processedParams.header_text"
          :key="`header-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.header_text[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2.5 text-sm font-semibold">
          Variáveis do Corpo da Mensagem
        </p>
        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
